import { Button, Divider, Flex, Text } from "@mantine/core";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import PlusIcon from "../common/Icons/PlusIcon";
import { theme } from "../../theme";
import DocumentUploadModal from "./DocumentUploadModal";
import TableDeleteButton from "../common/Buttons/TableDeleteButton";
import TableDownloadButton from "../common/Buttons/TableDownloadButton";
import AreYouSureModal from "../common/Modals/AreYouSureModal";
import {
  deleteEmployeeDocument,
  downloadEmployeeDocument,
  previewEmployeeDocument,
  uploadEmployeeDocument, 
} from "../../api/employeeApi";
import {
  deleteNotification,
  creationNotification,
} from "../../utils/useNotification";
import TablePreviewButton from "../common/Buttons/TablePreviewButton";
import styled from "styled-components";
import { media } from "../../theme";

const DocumentsTable = ({ documents, reFetchData }: any) => {
  const { id }: any = useParams();
  const navigate = useNavigate();
  const [itemToDeleteId, setitemToDeleteId] = useState<any>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const handleDeleteClick = async (docId: any) => {
    try {
      const { data }: any = await deleteEmployeeDocument({
        fileId: docId,
        employeeId: id,
      });
      deleteNotification(data?.success);
      reFetchData();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const requiredDocs = documents?.required || [];
  const otherDocs = documents?.other || [];

  return (
    <PageWrapper>
      <HeaderRow>
        <Text fw={600} fz={20} c="black">Özlük Belgeleri</Text>
        <Button
          color={theme.colors.brand.normal}
          leftSection={<PlusIcon />}
          onClick={() => navigate(`/employees/${id}/documents/upload`)}
        >
          Yeni Özlük Belgesi
        </Button>
      </HeaderRow>

      {/* Belgeler */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          width: "100%",
        }}
      >
        {/* Required Documents */}
        {requiredDocs.length > 0 && (
          <>
            <Divider my="md" label="Zorunlu Belgeler" labelPosition="center" />

            <DocGrid>
              {requiredDocs.map((doc: any) => (
                <DocCard key={doc.id}>
                  <Text ta="center">{doc?.name}</Text>
                  <Flex gap={8} justify="center">
                    {doc?.fileId ? (
                      <>
                        <TableDownloadButton
                          onClick={(e: any) => {
                            e.stopPropagation();
                            downloadEmployeeDocument(doc?.fileId);
                          }}
                        />
                        <TableDeleteButton
                          onClick={(e: any) => {
                            e.stopPropagation();
                            setitemToDeleteId(doc?.fileId);
                            setOpenDeleteModal(true);
                          }}
                        />
                        <TablePreviewButton
                          onClick={(e: any) => {
                            e.stopPropagation();
                            previewEmployeeDocument(doc?.fileId);
                          }}
                        />
                      </>
                    ) : (
                      <Button
                        color={theme.colors.brand.normal}
                        onClick={() => navigate(`/employees/${id}/documents/upload?docId=${doc.id}&docName=${encodeURIComponent(doc.name)}`)}
                      >
                        Yükle
                      </Button>
                    )}
                  </Flex>
                </DocCard>
              ))}
            </DocGrid>
          </>
        )}

        {/* Other Documents */}
        {otherDocs.length > 0 && (
          <>
            {requiredDocs.length > 0 && (
              <Divider my="md" label="Diğer Belgeler" labelPosition="center" />
            )}
            <DocGrid>
              {otherDocs.map((doc: any) => (
                <DocCard key={doc.fileId}>
                  <Text ta="center">{doc?.name}</Text>
                  <Flex gap={8} justify="center">
                    <TableDownloadButton
                      onClick={(e: any) => {
                        e.stopPropagation();
                        downloadEmployeeDocument(doc?.fileId);
                      }}
                    />
                    <TableDeleteButton
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setitemToDeleteId(doc?.fileId);
                        setOpenDeleteModal(true);
                      }}
                    />
                    <TablePreviewButton
                      onClick={(e: any) => {
                        e.stopPropagation();
                        previewEmployeeDocument(doc?.fileId);
                      }}
                    />
                  </Flex>
                  </DocCard>
                ))}
              </DocGrid>
            </>
          )}

          {/*
            Boş durum. Bu olmadan belgesi olmayan personelde ekran başlık +
            butondan ibaret kalıyordu ve "sayfa yüklenmedi mi?" hissi veriyordu:
            iki blok da `length > 0` ile korunduğu için hiçbir şey basılmıyor.
          */}
          {requiredDocs.length === 0 && otherDocs.length === 0 && (
            <EmptyState>
              Bu personel için henüz belge yüklenmemiş.{" "}
              <strong>Yeni Özlük Belgesi</strong> ile kimlik fotokopisi, diploma
              gibi belgeleri ekleyebilirsiniz.
            </EmptyState>
          )}
        </div>


      <AreYouSureModal
        opened={openDeleteModal}
        setOpened={setOpenDeleteModal}
        proccessFunc={() => handleDeleteClick(itemToDeleteId)}
        title="Belgeyi Sil"
        text="Bu belgeyi silmek istediğinizden emin misiniz?"
        confirmButtonText="Sil"
        cancelButtonText="Vazgeç"
      />
    </PageWrapper>
  );
};

export default DocumentsTable;

const EmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 32px;
  color: #6b7280;
  font-size: 15px;
  line-height: 22px;
`;

const HeaderRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  /* Dar ekranda başlık ile "Yeni Özlük Belgesi" birbirini eziyordu. */
  ${media.mobile} {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;

    button {
      width: 100%;
    }
  }
`;

const PageWrapper = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
`;

const DocGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
`;

const DocCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  aspect-ratio: 1 / 1;
  align-items: center;
  gap: 35px;
`;